'use client';

import { IcArrowRight, TabText, TabTextItem, Typography } from '@musinsa/mds';
import { NoticeBanner, useCommonLayout } from '@musinsa/mds-ui';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';
import type { SeedingProposalStatus, SeedingRecruitmentStatus } from '@musinsa/services-curator';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getPaths, pathsMusinsa } from '../../common';
import { curatorQueries } from '../../query-factory';
import { SNOWPLOW_SCHEME } from '../../snowplow';
import { useBrandsProposalUnviewed } from '../hooks/useBrandsProposalUnviewed';
import { useMyRecruitmentUnviewed } from '../hooks/useMyRecruitmentUnviewed';
import {
  CAMPAIGN_PROPOSAL_STATUS_APPLIED_TAB,
  CAMPAIGN_PROPOSAL_STATUS_LABELS,
  CAMPAIGN_PROPOSAL_TYPE_LABELS,
  isMusinsaService,
  type CampaignProposalStatus as ProposalStatus,
  type CampaignProposalType as ProposalType,
  trackCampaignCollaborationPlusClickMusinsa,
  trackCampaignCollaborationScreenViewMusinsa,
  trackCampaignCollaborationStatusTabClickMusinsa,
  trackCampaignCollaborationTopBannerClickMusinsa,
  trackCampaignCollaborationTypeTabClickMusinsa,
} from '../utils/campaignProposalMusinsaAnalytics';
import { buildCollaborateFilterSnowplowData } from '../utils/seedingCollaborateAnalytics';
import { DropDownMenu } from './DropDownMenu';
import { ProposalList } from './ProposalList';
import { ReverseProposalList } from './ReverseProposalList';
import { SeedingProposalList } from './SeedingProposalList';
import { SeedingRecruitmentList } from './SeedingRecruitmentList';

type CampaignCategory = 'COUPON' | 'SEEDING';

const CAMPAIGN_CATEGORY_LABELS: Record<CampaignCategory, string> = {
  COUPON: '쿠폰 캠페인',
  SEEDING: '시딩 캠페인',
};

// 상단 탭 표시 라벨 — 쿠폰/시딩 모두 동일(시딩 기준)으로 통일. (SID-4128)
// (analytics tabTitle 은 CAMPAIGN_PROPOSAL_TYPE_LABELS 유지 — 데이터 연속성)
const PROPOSAL_TYPE_TAB_LABELS: Record<ProposalType, string> = {
  MY_PROPOSAL: '내가 신청한 캠페인',
  BRANDS_PROPOSAL: '제안 받은 캠페인',
};

const SEEDING_RECRUITMENT_STATUS_LABELS: Record<SeedingRecruitmentStatus, string> = {
  PENDING: '신청',
  CONFIRMED: '진행',
  COMPLETED: '완료',
  NOT_SELECTED: '미선정',
};

const SEEDING_PROPOSAL_STATUS_LABELS: Record<SeedingProposalStatus, string> = {
  PENDING: '제안',
  CONFIRMED: '수락',
  COMPLETED: '완료',
  REJECTED: '거절',
};

const SEEDING_RECRUITMENT_STATUSES: SeedingRecruitmentStatus[] = [
  'PENDING',
  'CONFIRMED',
  'COMPLETED',
  'NOT_SELECTED',
];
const SEEDING_PROPOSAL_STATUSES: SeedingProposalStatus[] = [
  'PENDING',
  'CONFIRMED',
  'COMPLETED',
  'REJECTED',
];

const RECRUITMENT_COUNT_KEY: Record<
  SeedingRecruitmentStatus,
  'pending' | 'confirmed' | 'completed' | 'notSelected'
> = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  COMPLETED: 'completed',
  NOT_SELECTED: 'notSelected',
};
const PROPOSAL_COUNT_KEY: Record<
  SeedingProposalStatus,
  'pending' | 'confirmed' | 'completed' | 'rejected'
> = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  COMPLETED: 'completed',
  REJECTED: 'rejected',
};

const SORT_OPTIONS_MY = [
  { value: 'LATEST', label: '최신순' },
  { value: 'BRAND_NAME', label: '브랜드명순' },
];

const SORT_OPTIONS_BRANDS_WAITING = [
  { value: 'LATEST', label: '최신순' },
  { value: 'OLDEST', label: '오래된순' },
  { value: 'HIGH_DISCOUNT', label: '할인율 높은순' },
  { value: 'MANY_GOODS', label: '적용상품 많은순' },
  { value: 'ENDING_SOON', label: '종료 임박순' },
];

const SORT_OPTIONS_BRANDS_DEFAULT = [
  { value: 'LATEST', label: '최신순' },
  { value: 'OLDEST', label: '오래된순' },
];

const FILTER_OPTIONS = [
  { value: '', label: '전체상태' },
  { value: 'UPCOMING', label: '진행대기' },
  { value: 'ONGOING', label: '진행중' },
];

const getSortOptions = (type: ProposalType, status: ProposalStatus) => {
  if (type === 'MY_PROPOSAL') return SORT_OPTIONS_MY;
  if (status === 'WAITING') return SORT_OPTIONS_BRANDS_WAITING;
  return SORT_OPTIONS_BRANDS_DEFAULT;
};

export const CollaborationPageView = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();
  const { isMobile, service } = useDeviceInfo();
  const isPC = !isMobile;

  const typeFromUrl = searchParams.get('type');
  const statusFromUrl = searchParams.get('status');
  const sortFromUrl = searchParams.get('sort');
  const filterFromUrl = searchParams.get('filter') ?? '';
  const categoryFromUrl = searchParams.get('category');

  const is29CM = service === SERVICE['29CM'];
  const campaignCategory: CampaignCategory = categoryFromUrl === 'COUPON' ? 'COUPON' : 'SEEDING';
  const isSeeding = campaignCategory === 'SEEDING';

  const proposalType: ProposalType =
    typeFromUrl === 'BRANDS_PROPOSAL' || typeFromUrl === 'MY_PROPOSAL'
      ? typeFromUrl
      : 'BRANDS_PROPOSAL';

  const allowedStatuses = getStatusOptions(proposalType);
  const proposalStatus: ProposalStatus = allowedStatuses.includes(statusFromUrl as ProposalStatus)
    ? (statusFromUrl as ProposalStatus)
    : 'WAITING';

  const sortOptions = getSortOptions(proposalType, proposalStatus);
  const sort = sortOptions.some((o) => o.value === sortFromUrl)
    ? (sortFromUrl as string)
    : 'LATEST';
  const sortIndex = Math.max(
    0,
    sortOptions.findIndex((o) => o.value === sort),
  );

  const filter = FILTER_OPTIONS.some((o) => o.value === filterFromUrl) ? filterFromUrl : '';
  const filterIndex = Math.max(
    0,
    FILTER_OPTIONS.findIndex((o) => o.value === filter),
  );

  const routerRef = useRef(router);
  routerRef.current = router;

  const containers = useMemo<['gnb', 'fab', 'header']>(() => ['gnb', 'fab', 'header'], []);
  const options = useMemo(
    () => ({
      ...(isPC ? { contentSize: 'max600' as const } : {}),
      appbar: {
        use: true,
        useBackButton: true,
        title: '브랜드와 캠페인 협업하기',
      },
      gnb: { use: isPC },
      fab: {
        use: true,
        useSnapButton: proposalType === 'MY_PROPOSAL',
        customClickSnapButton: () => {
          if (isMusinsaService(service)) {
            trackCampaignCollaborationPlusClickMusinsa();
          }
          routerRef.current.push(pathsMusinsa.campaignSearch());
        },
        useInApp: true,
        useTopButton: true,
      },
    }),
    [isPC, proposalType, service],
  );

  useCommonLayout({ containers, options, isPcEnvironment: isPC });

  const [totalCount, setTotalCount] = useState(0);

  const BANNER_HEIGHT_PX = 58;
  const [stickyHeaderH, setStickyHeaderH] = useState<number | null>(null);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const compute = () => {
      const header = document.querySelector('header.sticky') as HTMLElement | null;
      if (header) return header.getBoundingClientRect().height;
      const gnb = document.getElementById('commonLayoutGnb');
      const hdr = document.getElementById('commonLayoutHeader');
      return (
        (gnb?.getBoundingClientRect().height ?? 0) + (hdr?.getBoundingClientRect().height ?? 0)
      );
    };
    setStickyHeaderH(compute());
    const ro = new ResizeObserver(() => setStickyHeaderH(compute()));
    document
      .querySelectorAll('header.sticky, #commonLayoutGnb, #commonLayoutHeader')
      .forEach((el) => ro.observe(el));
    return () => ro.disconnect();
  }, []);
  const stickyEnabled = stickyHeaderH !== null;
  const showGuideBanner = !is29CM;
  const stickyTopBanner = stickyHeaderH ?? 0;
  const stickyTopBelowBanner = (stickyHeaderH ?? 0) + (showGuideBanner ? BANNER_HEIGHT_PX : 0);

  const seedingStatusFromUrl = searchParams.get('seedingStatus');
  const seedingRecruitmentStatus: SeedingRecruitmentStatus = SEEDING_RECRUITMENT_STATUSES.includes(
    seedingStatusFromUrl as SeedingRecruitmentStatus,
  )
    ? (seedingStatusFromUrl as SeedingRecruitmentStatus)
    : 'PENDING';
  const seedingProposalStatus: SeedingProposalStatus = SEEDING_PROPOSAL_STATUSES.includes(
    seedingStatusFromUrl as SeedingProposalStatus,
  )
    ? (seedingStatusFromUrl as SeedingProposalStatus)
    : 'PENDING';

  const { data: seedingRecruitmentCount } = useQuery({
    ...curatorQueries.campaigns.seedingMeRecruitmentCount(),
    enabled: isSeeding,
  });
  const { data: seedingProposalCount } = useQuery({
    ...curatorQueries.campaigns.seedingMeProposalCount(),
    enabled: isSeeding,
  });
  const { hasUnviewed: hasNewSeedingProposal, markAsViewed: markBrandsProposalViewed } =
    useBrandsProposalUnviewed();
  const { hasUnviewed: hasNewSeedingRecruitment, markAsViewed: markMyRecruitmentViewed } =
    useMyRecruitmentUnviewed();

  // 진입 시점의 "신규 미확인" 여부를 1회 고정한다. 아래 markAsViewed 가 watermark 를 최신으로 갱신해
  // hasUnviewed 가 즉시 false 가 되더라도, 탭 dot 은 이 진입에서 신규가 있었음을 계속 표시한다.
  // (홈 dot 은 watermark 갱신으로 정상 클리어 — CMQA-7180. 이 고정값이 없으면 진입 즉시 marking 이
  //  탭 dot 을 지워, 홈은 뜨는데 협업 탭은 안 뜨는 결함이 된다.)
  const [proposalUnviewedOnEntry, setProposalUnviewedOnEntry] = useState(false);
  const [recruitmentUnviewedOnEntry, setRecruitmentUnviewedOnEntry] = useState(false);
  useEffect(() => {
    if (hasNewSeedingProposal) setProposalUnviewedOnEntry(true);
  }, [hasNewSeedingProposal]);
  useEffect(() => {
    if (hasNewSeedingRecruitment) setRecruitmentUnviewedOnEntry(true);
  }, [hasNewSeedingRecruitment]);

  useEffect(() => {
    if (isSeeding && proposalType === 'BRANDS_PROPOSAL') {
      markBrandsProposalViewed();
    }
    if (isSeeding && proposalType === 'MY_PROPOSAL') {
      markMyRecruitmentViewed();
    }
  }, [isSeeding, proposalType, markBrandsProposalViewed, markMyRecruitmentViewed]);

  // 재가시 시 제안/신청 목록·카운트 무효화 — 응답 기한 만료 등 mutation 없이 BE 가 목록에서
  // 필터링하는 변화를 반영한다. 29CM 은 상세를 openWebview(별도 웹뷰)로 열어 목록이 마운트된 채
  // 남고 remount/focus refetch 가 없어, 마감 캠페인이 stale 캐시로 잔존했다(QueryClient
  // refetchOnWindowFocus:false). 재가시 invalidate 로 복귀 시점에 정리한다. (CMQA-7164 이슈2 / CMQA-7172 선례)
  useEffect(() => {
    const revalidate = () => {
      if (document.visibilityState !== 'visible') return;
      queryClient.invalidateQueries({ queryKey: curatorQueries.campaigns.seedingMeProposal._def });
      queryClient.invalidateQueries({
        queryKey: curatorQueries.campaigns.seedingMeRecruitment._def,
      });
      queryClient.invalidateQueries({
        queryKey: curatorQueries.campaigns.seedingMeProposalCount().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: curatorQueries.campaigns.seedingMeRecruitmentCount().queryKey,
      });
    };
    document.addEventListener('visibilitychange', revalidate);
    window.addEventListener('focus', revalidate); // 무신사 SPA·web 복귀 보강
    return () => {
      document.removeEventListener('visibilitychange', revalidate);
      window.removeEventListener('focus', revalidate);
    };
  }, [queryClient]);

  const seedingListCount = isSeeding
    ? proposalType === 'MY_PROPOSAL'
      ? (seedingRecruitmentCount?.[RECRUITMENT_COUNT_KEY[seedingRecruitmentStatus]] ?? 0)
      : (seedingProposalCount?.[PROPOSAL_COUNT_KEY[seedingProposalStatus]] ?? 0)
    : 0;

  useEffect(() => {
    if (!isMusinsaService(service)) return;
    trackCampaignCollaborationScreenViewMusinsa();
  }, [service]);

  const paths = getPaths(service);
  const updateQuery = useCallback(
    (next: {
      type?: ProposalType;
      status?: ProposalStatus;
      sort?: string;
      filter?: string;
      category?: CampaignCategory;
      seedingStatus?: SeedingRecruitmentStatus | SeedingProposalStatus;
    }) => {
      router.replace(
        paths.campaignCollaboration({
          query: {
            type: next.type ?? proposalType,
            status: next.status ?? proposalStatus,
            sort: next.sort ?? sort,
            filter: next.filter ?? filter,
            category: next.category ?? campaignCategory,
            seedingStatus:
              next.seedingStatus ??
              (proposalType === 'MY_PROPOSAL' ? seedingRecruitmentStatus : seedingProposalStatus),
          },
        }),
      );
    },
    [
      router,
      proposalType,
      proposalStatus,
      sort,
      filter,
      campaignCategory,
      seedingRecruitmentStatus,
      seedingProposalStatus,
    ],
  );

  const handleTypeChange = useCallback(
    (type: ProposalType) => {
      if (isMusinsaService(service)) {
        trackCampaignCollaborationTypeTabClickMusinsa({
          tabTitle: CAMPAIGN_PROPOSAL_TYPE_LABELS[type],
        });
      }
      // 협업하기(시딩) > 모집 유형 필터(1depth) 클릭 (29CM Snowplow) — IMPLEMENTATION.md ③ #1
      // 🚩 depth↔UI 매핑 잠정 (내가신청한/제안받은 탭 = 모집 유형). hand-off 확인 대상.
      if (isSeeding && service === SERVICE['29CM']) {
        track({
          schema: SNOWPLOW_SCHEME.CLICK_BUTTON,
          data: buildCollaborateFilterSnowplowData({
            buttonId: 'curator_recruitment_type_filter',
            buttonName: PROPOSAL_TYPE_TAB_LABELS[type],
          }),
        });
      }
      updateQuery({ type, status: 'WAITING', sort: 'LATEST', filter: '' });
    },
    [service, isSeeding, updateQuery],
  );

  // 협업하기(시딩) > 캠페인 상태 필터 클릭 (29CM Snowplow) — IMPLEMENTATION.md ③ #2/#4
  // 🚩 depth↔UI 매핑 잠정: 내가신청한=2depth(status_filter), 제안받은=3depth(status_filter2). hand-off.
  const handleSeedingStatusChange = useCallback(
    (status: SeedingRecruitmentStatus | SeedingProposalStatus, statusLabel: string) => {
      if (service === SERVICE['29CM']) {
        track({
          schema: SNOWPLOW_SCHEME.CLICK_BUTTON,
          data: buildCollaborateFilterSnowplowData({
            buttonId:
              proposalType === 'BRANDS_PROPOSAL'
                ? 'curator_campaign_status_filter2'
                : 'curator_campaign_status_filter',
            buttonName: statusLabel,
          }),
        });
      }
      updateQuery({ seedingStatus: status });
    },
    [service, proposalType, updateQuery],
  );

  const handleStatusChange = useCallback(
    (status: ProposalStatus) => {
      if (isMusinsaService(service)) {
        trackCampaignCollaborationStatusTabClickMusinsa({
          appliedTab: CAMPAIGN_PROPOSAL_STATUS_APPLIED_TAB[proposalStatus],
          tabTitle: CAMPAIGN_PROPOSAL_STATUS_LABELS[status],
          type: proposalType === 'MY_PROPOSAL' ? 'curator' : 'brand',
        });
      }
      updateQuery({ status, sort: 'LATEST', filter: '' });
    },
    [service, proposalType, proposalStatus, updateQuery],
  );

  const statusOptions = getStatusOptions(proposalType);

  const showFilter = proposalType === 'BRANDS_PROPOSAL' && proposalStatus === 'APPROVED';

  return (
    <div
      className={`flex flex-col bg-white ${
        is29CM ? 'flex-1' : '[min-height:calc(100vh-var(--layout-header-top)-52px)]'
      }`}
    >
      {showGuideBanner && (
        <div
          className={stickyEnabled ? 'sticky z-20 bg-white' : 'z-20 bg-white'}
          style={stickyEnabled ? { top: stickyTopBanner } : undefined}
        >
          <Link
            href={pathsMusinsa.campaignCouponGuide()}
            onClick={() => {
              if (!isMusinsaService(service)) return;
              trackCampaignCollaborationTopBannerClickMusinsa({
                appliedTab: CAMPAIGN_PROPOSAL_STATUS_APPLIED_TAB[proposalStatus],
                title: '쿠폰 사용 및 활용 가이드를 확인하세요.',
              });
            }}
          >
            <div className="px-4 pt-3 pb-1 bg-gray-100">
              <NoticeBanner
                label="GUIDE"
                text="쿠폰 사용 및 활용 가이드를 확인하세요."
                right={
                  <div className="w-4">
                    <IcArrowRight strokeColor="gray-500" />
                  </div>
                }
              />
            </div>
          </Link>
        </div>
      )}

      <div className="bg-white">
        <TabText colorScheme="gray" depth="oneDepth" divider="on">
          {(['MY_PROPOSAL', 'BRANDS_PROPOSAL'] as const).map((type) => (
            <TabTextItem
              key={type}
              isSelected={proposalType === type}
              disabledUnderline={false}
              onClick={() => handleTypeChange(type)}
            >
              <span className="inline-flex items-start">
                {/* 쿠폰/시딩 탭 라벨 통일(시딩 기준). (SID-4128) */}
                {PROPOSAL_TYPE_TAB_LABELS[type]}
                {/* 블루닷 — 신청/제안(pending) 0개면 미노출. (제안형 SID-4120 / 모집형 SID-4312)
                    진입 즉시 markAsViewed 가 watermark 를 갱신하므로, 라이브 hasUnviewed 대신
                    진입-시점 고정값을 써야 탭 dot 이 노출된다. (홈은 노출/메뉴 탭 미노출 결함 수정) */}
                {isSeeding &&
                  type === 'BRANDS_PROPOSAL' &&
                  proposalUnviewedOnEntry &&
                  (seedingProposalCount?.pending ?? 0) > 0 && (
                    <span className="bg-[#245eff] rounded-full size-[4px] ml-[2px] mt-[2px] inline-block shrink-0" />
                  )}
                {isSeeding &&
                  type === 'MY_PROPOSAL' &&
                  recruitmentUnviewedOnEntry &&
                  (seedingRecruitmentCount?.pending ?? 0) > 0 && (
                    <span className="bg-[#245eff] rounded-full size-[4px] ml-[2px] mt-[2px] inline-block shrink-0" />
                  )}
              </span>
            </TabTextItem>
          ))}
        </TabText>
        {!is29CM && (
          <TabText colorScheme="gray" depth="twoDepth" divider="on">
            {(['COUPON', 'SEEDING'] as const).map((category) => (
              <TabTextItem
                key={category}
                disabledUnderline={true}
                isSelected={campaignCategory === category}
                onClick={() => updateQuery({ category, seedingStatus: 'PENDING' })}
              >
                {CAMPAIGN_CATEGORY_LABELS[category]}
              </TabTextItem>
            ))}
          </TabText>
        )}
      </div>

      <div
        className={stickyEnabled ? 'sticky z-20 bg-white' : 'z-20 bg-white'}
        style={stickyEnabled ? { top: stickyTopBelowBanner } : undefined}
      >
        {isSeeding ? (
          <TabText colorScheme="gray" depth="twoDepth" divider="on">
            {(proposalType === 'MY_PROPOSAL'
              ? SEEDING_RECRUITMENT_STATUSES
              : SEEDING_PROPOSAL_STATUSES
            ).map((status) => (
              <TabTextItem
                key={status}
                disabledUnderline={true}
                isSelected={
                  (proposalType === 'MY_PROPOSAL'
                    ? seedingRecruitmentStatus
                    : seedingProposalStatus) === status
                }
                onClick={() =>
                  handleSeedingStatusChange(
                    status,
                    proposalType === 'MY_PROPOSAL'
                      ? SEEDING_RECRUITMENT_STATUS_LABELS[status as SeedingRecruitmentStatus]
                      : SEEDING_PROPOSAL_STATUS_LABELS[status as SeedingProposalStatus],
                  )
                }
              >
                {proposalType === 'MY_PROPOSAL'
                  ? SEEDING_RECRUITMENT_STATUS_LABELS[status as SeedingRecruitmentStatus]
                  : SEEDING_PROPOSAL_STATUS_LABELS[status as SeedingProposalStatus]}
              </TabTextItem>
            ))}
          </TabText>
        ) : (
          <TabText colorScheme="gray" depth="twoDepth" divider="on">
            {statusOptions.map((status) => (
              <TabTextItem
                key={status}
                disabledUnderline={true}
                isSelected={proposalStatus === status}
                onClick={() => handleStatusChange(status)}
              >
                {CAMPAIGN_PROPOSAL_STATUS_LABELS[status]}
              </TabTextItem>
            ))}
          </TabText>
        )}
        {isSeeding && (
          // 개수 표기 영역 height 42px. (SID-4419 코멘트)
          <div className="px-[16px]! h-[42px]! flex items-center bg-gray-100">
            <Typography variant="body13pxReg" colorScheme="gray-600">
              {seedingListCount}개
            </Typography>
          </div>
        )}
      </div>

      {!isSeeding && (
        <div className="flex items-center justify-between px-4 py-3 bg-gray-100">
          <Typography variant="body13pxReg" colorScheme="gray-600">
            {totalCount}개
          </Typography>
          <div className="flex items-center gap-2">
            {showFilter && (
              <DropDownMenu
                items={FILTER_OPTIONS}
                selectedIndex={filterIndex}
                onSelect={(value) => updateQuery({ filter: value })}
              />
            )}
            <DropDownMenu
              items={sortOptions}
              selectedIndex={sortIndex}
              onSelect={(value) => updateQuery({ sort: value })}
            />
          </div>
        </div>
      )}

      {/* PLP 하단 safe-area 처리 (72px 하단여백은 각 리스트가 마지막 항목에 부여). (피드백 항목18) */}
      <div className="flex-1 flex flex-col safe-area-bottom">
        {isSeeding ? (
          proposalType === 'MY_PROPOSAL' ? (
            <SeedingRecruitmentList status={seedingRecruitmentStatus} />
          ) : (
            <SeedingProposalList status={seedingProposalStatus} />
          )
        ) : proposalType === 'MY_PROPOSAL' ? (
          <ReverseProposalList
            status={proposalStatus as 'WAITING' | 'APPROVED' | 'REJECTED'}
            sort={sort as 'LATEST' | 'BRAND_NAME'}
            onTotalCountChange={setTotalCount}
          />
        ) : (
          <ProposalList
            status={proposalStatus}
            progressStatus={filter === 'UPCOMING' || filter === 'ONGOING' ? filter : undefined}
            sort={
              sort as
                | 'LATEST'
                | 'OLDEST'
                | 'BRAND_NAME'
                | 'HIGH_DISCOUNT'
                | 'MANY_GOODS'
                | 'ENDING_SOON'
            }
            onTotalCountChange={setTotalCount}
          />
        )}
      </div>
    </div>
  );
};

function getStatusOptions(type: ProposalType): ProposalStatus[] {
  if (type === 'MY_PROPOSAL') {
    return ['WAITING', 'APPROVED', 'REJECTED'];
  }
  return ['WAITING', 'APPROVED', 'COMPLETED', 'REJECTED'];
}
